import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export const LanguageSelector = ({ compact = false }) => {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-white/15 bg-white/[0.04] backdrop-blur-md px-3 py-2 text-white/80 hover:text-white hover:border-[var(--al-gold)]/50 transition-colors duration-300"
        data-testid="language-selector-trigger"
        aria-label="Select language"
      >
        <Globe className="w-3.5 h-3.5 text-[var(--al-gold)]" />
        <span className="font-mono-tech text-[12px] tracking-[0.15em]">{current.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute ${compact ? "left-0" : "right-0"} mt-2 min-w-[168px] bg-[#101010]/95 backdrop-blur-2xl border border-white/10 shadow-2xl z-[120] overflow-hidden`}
            data-testid="language-selector-menu"
          >
            {languages.map((l) => {
              const active = l.code === lang;
              return (
                <li key={l.code}>
                  <button
                    onClick={() => {
                      setLang(l.code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-4 px-4 py-3 text-left transition-colors duration-200 ${
                      active ? "bg-[var(--al-gold)]/10 text-[var(--al-gold)]" : "text-white/70 hover:bg-white/[0.05] hover:text-white"
                    }`}
                    data-testid={`language-option-${l.code}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="font-mono-tech text-[11px] tracking-[0.15em] w-6">{l.label}</span>
                      <span className="font-body text-[13px]">{l.name}</span>
                    </span>
                    {active && <Check className="w-3.5 h-3.5" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
